/**
 * widerruf-form.js
 * ────────────────────────────────────────────────────────────────────────
 * Sendet Widerruf-/Kündigungs-Anfragen via Klaviyo Client Events API
 * an einen Klaviyo-Flow, der 2 Mails auslöst:
 *   1. Empfangsbestätigung an den Kunden (gesetzliche Pflicht § 13a FAGG)
 *   2. Notification an marcel@waschma.com (für manuelle Bearbeitung)
 *
 * Klaviyo Custom-Event: "Widerruf-Antrag eingereicht"
 * Profile wird automatisch via Email gemerged.
 */

(function () {
  'use strict';

  const KLAVIYO_COMPANY_ID = 'VKQMJQ';
  const EVENT_NAME = 'Widerruf-Antrag eingereicht';
  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_ENDPOINT = `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`;

  function init() {
    const forms = document.querySelectorAll('#widerruf-form');
    forms.forEach((form) => {
      if (form._widerrufInit) return;
      form._widerrufInit = true;
      form.addEventListener('submit', handleSubmit);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const submitBtn = form.querySelector('button[type="submit"]');

    const anliegen = getValue(form, 'anliegen') || 'Widerruf';
    const name = getValue(form, 'name');
    const email = getValue(form, 'email');
    const orderNumber = getValue(form, 'order_number');
    const message = getValue(form, 'message');
    const notificationEmail = form.querySelector('[name="notification_email"]')?.value || 'marcel@waschma.com';

    if (!name) {
      showError(form, 'Bitte gib deinen Namen ein.');
      form.querySelector('[name="name"]')?.focus();
      return;
    }
    if (!email || !isValidEmail(email)) {
      showError(form, 'Bitte gib eine gültige E-Mail-Adresse ein.');
      form.querySelector('[name="email"]')?.focus();
      return;
    }
    if (!orderNumber) {
      showError(form, 'Bitte gib deine Bestellnummer ein.');
      form.querySelector('[name="order_number"]')?.focus();
      return;
    }

    clearError(form);

    const originalText = submitBtn?.textContent.trim();
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = form.dataset.loadingText || 'Bitte warten...';
    }

    const now = new Date();
    const timestampISO = now.toISOString();
    const timestampHuman = formatGermanTimestamp(now);
    const nameParts = name.split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || null;

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            anliegen,
            name,
            bestellnummer: orderNumber,
            freitext: message || '(keine Nachricht)',
            timestamp_iso: timestampISO,
            timestamp_human: timestampHuman,
            notification_email: notificationEmail,
            quelle: 'Widerruf-Formular Footer',
            rechtsgrundlage: '§ 13a FAGG',
          },
          metric: {
            data: {
              type: 'metric',
              attributes: { name: EVENT_NAME },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                first_name: firstName,
                last_name: lastName,
                properties: {
                  last_widerruf_request_at: timestampISO,
                  last_widerruf_anliegen: anliegen,
                  last_widerruf_order: orderNumber,
                },
              },
            },
          },
          time: timestampISO,
          unique_id: `widerruf_${email}_${now.getTime()}`,
        },
      },
    };

    try {
      const res = await fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok || res.status === 202) {
        showSuccess(form, timestampHuman);
      } else {
        const text = await res.text().catch(() => '');
        console.error('[widerruf-form] Klaviyo error', res.status, text);
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('[widerruf-form] Submission failed:', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = originalText || 'Anfrage absenden';
      }
      showError(
        form,
        form.dataset.errorMessage ||
          'Ups, es ist ein Fehler aufgetreten. Bitte versuche es später nocheinmal oder schreibe direkt an office@waschma.com.'
      );
    }
  }

  function getValue(form, name) {
    return (form.querySelector(`[name="${name}"]`)?.value || '').trim();
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function formatGermanTimestamp(date) {
    try {
      return (
        date.toLocaleString('de-AT', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Vienna',
        }) + ' Uhr'
      );
    } catch (e) {
      return date.toISOString();
    }
  }

  function showSuccess(form, timestamp) {
    const heading = form.dataset.successHeading || 'Anfrage eingegangen';
    const message =
      form.dataset.successMessage ||
      'Dein Widerruf ist eingegangen und mit Absenden wirksam. Du erhältst gleich eine Empfangsbestätigung per E-Mail. Die Rückzahlung erfolgt binnen 14 Kalendertagen ab Eingang dieser Erklärung (§ 14 FAGG).';
    const infoText =
      form.dataset.successInfo ||
      'Eingangszeit (für deine Unterlagen):';

    const wrap = document.createElement('div');
    wrap.className = 'widerruf__success';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');

    const headingEl = document.createElement('h2');
    headingEl.className = 'widerruf__success-heading';
    headingEl.textContent = heading;
    wrap.appendChild(headingEl);

    const msgEl = document.createElement('p');
    msgEl.className = 'widerruf__success-message';
    msgEl.textContent = message;
    wrap.appendChild(msgEl);

    const tsEl = document.createElement('p');
    tsEl.className = 'widerruf__success-timestamp';
    const tsLabel = document.createElement('span');
    tsLabel.textContent = infoText + ' ';
    const tsValue = document.createElement('strong');
    tsValue.textContent = timestamp;
    tsEl.appendChild(tsLabel);
    tsEl.appendChild(tsValue);
    wrap.appendChild(tsEl);

    form.replaceWith(wrap);
    wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function showError(form, message) {
    let errEl = form.querySelector('[data-widerruf-error]');
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.setAttribute('data-widerruf-error', '');
      errEl.className = 'widerruf-form__error';
      errEl.setAttribute('role', 'alert');
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn?.parentNode) {
        submitBtn.parentNode.insertBefore(errEl, submitBtn);
      } else {
        form.appendChild(errEl);
      }
    }
    errEl.textContent = message;
    errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearError(form) {
    const errEl = form.querySelector('[data-widerruf-error]');
    if (errEl) errEl.remove();
  }
})();
